import { useState } from 'react';
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import SuperAdminLayout from "@/components/admin/super-admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  Shield, 
  AlertTriangle,
  Filter,
  RefreshCw,
  Download,
  MoreHorizontal,
  Calendar,
  Info,
  Clock,
  XCircle,
  CheckCircle,
  Flag,
  Eye,
  Settings,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';

// Define types
interface FraudEvent {
  id: string;
  type: 'radar.early_fraud_warning' | 'charge.dispute.created' | 'payment.blocked' | 'payment.flagged' | 'manual.flag';
  description: string;
  tenantId: number;
  tenantName: string;
  userId: number | null;
  userName: string | null;
  ipAddress: string | null;
  amount: number | null;
  currency: string | null;
  severity: 'high' | 'medium' | 'low';
  status: 'new' | 'reviewing' | 'resolved' | 'dismissed';
  stripeEventId: string | null;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, any>;
}

interface FraudStats {
  totalEvents: number;
  newEvents: number;
  resolvedEvents: number;
  dismissedEvents: number;
  reviewingEvents: number;
  highSeverityEvents: number;
  recentEvents: number;
  blockedPaymentAmount: number;
  riskScore: number;
}

interface BlockedIP {
  id: number;
  ipAddress: string;
  reason: string;
  createdAt: string;
  blockExpires: string | null;
  totalAttempts: number;
}

interface BlockedUser {
  id: number;
  userId: number;
  userName: string;
  reason: string;
  createdAt: string;
  totalAttempts: number;
}

export default function FraudMonitoringPage() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<FraudEvent | null>(null);
  const [showBlockAddressDialog, setShowBlockAddressDialog] = useState(false);
  const [blockIPForm, setBlockIPForm] = useState({
    ipAddress: '',
    reason: '',
    expiration: 'never'
  });
  
  // Fetch fraud events
  const { data: fraudEvents = [], isLoading: eventsLoading } = useQuery<FraudEvent[]>({
    queryKey: ["/api/admin/fraud/events"],
    queryFn: async () => {
      try {
        const response = await apiRequest<Response>("/api/admin/fraud/events", {
          method: "GET",
        });
        return response.json();
      } catch (error) {
        console.error("Error fetching fraud events:", error);
        // Return empty array on error
        return [];
      }
    },
  });

  // Fetch fraud stats
  const { data: fraudStats, isLoading: statsLoading } = useQuery<FraudStats>({
    queryKey: ["/api/admin/fraud/stats"],
    queryFn: async () => {
      try {
        const response = await apiRequest<Response>("/api/admin/fraud/stats", {
          method: "GET",
        });
        return response.json();
      } catch (error) {
        console.error("Error fetching fraud stats:", error);
        // Return zero stats on error
        return {
          totalEvents: 0,
          newEvents: 0,
          resolvedEvents: 0,
          dismissedEvents: 0,
          reviewingEvents: 0,
          highSeverityEvents: 0,
          recentEvents: 0,
          blockedPaymentAmount: 0,
          riskScore: 0
        };
      }
    },
  });

  // Fetch blocked IPs
  const { data: blockedIPs = [], isLoading: blockedIPsLoading } = useQuery<BlockedIP[]>({
    queryKey: ["/api/admin/fraud/blocked-ips"],
    queryFn: async () => {
      try {
        const response = await apiRequest<Response>("/api/admin/fraud/blocked-ips", {
          method: "GET",
        });
        return response.json();
      } catch (error) {
        console.error("Error fetching blocked IPs:", error);
        // Return empty array on error
        return [];
      }
    },
  });

  // Fetch blocked users
  const { data: blockedUsers = [], isLoading: blockedUsersLoading } = useQuery<BlockedUser[]>({
    queryKey: ["/api/admin/fraud/blocked-users"],
    queryFn: async () => {
      try {
        const response = await apiRequest<Response>("/api/admin/fraud/blocked-users", {
          method: "GET",
        });
        return response.json();
      } catch (error) {
        console.error("Error fetching blocked users:", error);
        // Return empty array on error
        return [];
      }
    },
  });

  // Filter fraud events based on search query and filters
  const filteredEvents = fraudEvents.filter(event => {
    // Apply search filter
    const matchesSearch = 
      event.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (event.userName && event.userName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (event.ipAddress && event.ipAddress.includes(searchQuery));
    
    // Apply status filter
    const matchesStatus = statusFilter === "all" || event.status === statusFilter;
    
    // Apply severity filter
    const matchesSeverity = severityFilter === "all" || event.severity === severityFilter;
    
    return matchesSearch && matchesStatus && matchesSeverity;
  });

  // Format currency
  const formatCurrency = (amount: number | null, currency: string = 'USD') => {
    if (amount === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };

  // Generate status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">New</Badge>;
      case 'reviewing':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Reviewing</Badge>;
      case 'resolved':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Resolved</Badge>;
      case 'dismissed':
        return <Badge variant="outline" className="text-slate-500">Dismissed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Generate severity badge
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'high':
        return <Badge variant="destructive">High</Badge>;
      case 'medium':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Medium</Badge>;
      case 'low':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Low</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  // Generate type badge
  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'radar.early_fraud_warning':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Early Fraud Warning</Badge>;
      case 'charge.dispute.created':
        return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">Dispute</Badge>;
      case 'payment.blocked':
        return <Badge className="bg-slate-100 text-slate-800 hover:bg-slate-100">Blocked Payment</Badge>;
      case 'payment.flagged':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Flagged Payment</Badge>;
      case 'manual.flag':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Manual Flag</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  // Handle update status of fraud event
  const handleUpdateEventStatus = (eventId: string, newStatus: string) => {
    toast({
      title: "Status updated",
      description: `Event status changed to ${newStatus}`,
    });
  };

  // Handle block IP address
  const handleBlockIPAddress = () => {
    toast({
      title: "IP blocked",
      description: `IP address ${blockIPForm.ipAddress} has been blocked`,
    });
    setShowBlockAddressDialog(false);
    setBlockIPForm({
      ipAddress: '',
      reason: '',
      expiration: 'never'
    });
  };

  return (
    <SuperAdminLayout>
      <div className="container mx-auto p-4 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Fraud Monitoring</h1>
            <p className="text-muted-foreground">
              Monitor and manage potential fraud activities
            </p>
          </div>
          
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              onClick={() => window.open('https://dashboard.stripe.com/radar', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Stripe Radar
            </Button>
            <Button onClick={() => { setShowBlockAddressDialog(true) }}>
              <BlockIcon className="h-4 w-4 mr-2" />
              Block IP Address
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                New Fraud Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <AlertTriangle className="mr-2 h-4 w-4 text-amber-500" />
                <div className="text-2xl font-bold">{statsLoading ? "-" : fraudStats?.newEvents}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                High Severity Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Shield className="mr-2 h-4 w-4 text-red-500" />
                <div className="text-2xl font-bold">{statsLoading ? "-" : fraudStats?.highSeverityEvents}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Blocked Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <BlockIcon className="mr-2 h-4 w-4 text-slate-500" />
                <div className="text-2xl font-bold">
                  {statsLoading ? "-" : formatCurrency(fraudStats?.blockedPaymentAmount || 0)}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                System Risk Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {statsLoading ? "-" : fraudStats?.riskScore || 0}/100
              </div>
              <Progress 
                value={statsLoading ? 0 : fraudStats?.riskScore || 0} 
                className={`h-2 mt-2 ${
                  fraudStats?.riskScore && fraudStats.riskScore > 70 ? "bg-red-500" : 
                  fraudStats?.riskScore && fraudStats.riskScore > 40 ? "bg-amber-500" :
                  "bg-green-500"
                }`}
              />
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="events">
          <TabsList className="mb-4">
            <TabsTrigger value="events">Fraud Events</TabsTrigger>
            <TabsTrigger value="blocked">Blocked IPs & Users</TabsTrigger>
            <TabsTrigger value="settings">Fraud Settings</TabsTrigger>
          </TabsList>
          
          {/* Fraud Events Tab */}
          <TabsContent value="events" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search events..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="flex space-x-2">
                <div className="flex items-center space-x-1">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Filter:</span>
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[130px] h-9">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={severityFilter} onValueChange={setSeverityFilter}>
                  <SelectTrigger className="w-[130px] h-9">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severity</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="outline" className="sm:ml-auto">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
            
            <Card>
              <CardContent className="p-0">
                {eventsLoading ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-10 h-10 rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading fraud events...</p>
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <div className="text-center py-10">
                    <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <h3 className="text-lg font-medium mb-1">No fraud events found</h3>
                    <p className="text-muted-foreground">
                      {searchQuery || statusFilter !== "all" || severityFilter !== "all"
                        ? `No results found with the current filters`
                        : "There are no fraud events in the system yet"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Tenant</TableHead>
                          <TableHead>User / IP</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Severity</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEvents.map((event) => (
                          <TableRow key={event.id} className="cursor-pointer hover:bg-muted/50">
                            <TableCell className="font-medium max-w-[200px] truncate" onClick={() => {
                              setSelectedEvent(event);
                              setShowEventDetails(true);
                            }}>
                              {event.description}
                            </TableCell>
                            <TableCell>{getTypeBadge(event.type)}</TableCell>
                            <TableCell>{event.tenantName}</TableCell>
                            <TableCell className="text-xs">
                              {event.userName && <div>{event.userName}</div>}
                              {event.ipAddress && <div className="text-muted-foreground">{event.ipAddress}</div>}
                            </TableCell>
                            <TableCell>{event.amount !== null ? formatCurrency(event.amount, event.currency || 'USD') : "N/A"}</TableCell>
                            <TableCell>{getSeverityBadge(event.severity)}</TableCell>
                            <TableCell>{getStatusBadge(event.status)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <span className="text-xs">{new Date(event.createdAt).toLocaleDateString()}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => {
                                    setSelectedEvent(event);
                                    setShowEventDetails(true);
                                  }}>
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  
                                  {event.status === "new" && (
                                    <DropdownMenuItem 
                                      onClick={() => handleUpdateEventStatus(event.id, "reviewing")}
                                    >
                                      <Info className="mr-2 h-4 w-4" />
                                      Mark as Reviewing
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {(event.status === "new" || event.status === "reviewing") && (
                                    <DropdownMenuItem 
                                      onClick={() => handleUpdateEventStatus(event.id, "resolved")}
                                    >
                                      <CheckCircle className="mr-2 h-4 w-4" />
                                      Mark as Resolved
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {(event.status === "new" || event.status === "reviewing") && (
                                    <DropdownMenuItem 
                                      onClick={() => handleUpdateEventStatus(event.id, "dismissed")}
                                    >
                                      <XCircle className="mr-2 h-4 w-4" />
                                      Dismiss
                                    </DropdownMenuItem>
                                  )}
                                  
                                  <DropdownMenuSeparator />
                                  
                                  {event.ipAddress && (
                                    <DropdownMenuItem 
                                      className="text-red-600"
                                      onClick={() => {
                                        setBlockIPForm({
                                          ...blockIPForm,
                                          ipAddress: event.ipAddress || ''
                                        });
                                        setShowBlockAddressDialog(true);
                                      }}
                                    >
                                      <BlockIcon className="mr-2 h-4 w-4" />
                                      Block IP Address
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {event.userId && (
                                    <DropdownMenuItem 
                                      className="text-red-600"
                                    >
                                      <Flag className="mr-2 h-4 w-4" />
                                      Flag User
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Blocked IPs & Users Tab */}
          <TabsContent value="blocked" className="space-y-4">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Blocked IP Addresses */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Blocked IP Addresses</CardTitle>
                      <CardDescription>
                        IP addresses that are blocked from accessing the platform
                      </CardDescription>
                    </div>
                    <Button size="sm" onClick={() => setShowBlockAddressDialog(true)}>
                      <BlockIcon className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {blockedIPsLoading ? (
                    <div className="text-center py-10">
                      <div className="animate-spin w-8 h-8 rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Loading blocked IPs...</p>
                    </div>
                  ) : blockedIPs.length === 0 ? (
                    <div className="text-center py-10">
                      <BlockIcon className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                      <h3 className="text-base font-medium mb-1">No blocked IPs</h3>
                      <p className="text-sm text-muted-foreground">
                        There are no blocked IP addresses
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>IP Address</TableHead>
                            <TableHead>Reason</TableHead>
                            <TableHead>Date Blocked</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {blockedIPs.map((ip) => (
                            <TableRow key={ip.id}>
                              <TableCell className="font-medium">{ip.ipAddress}</TableCell>
                              <TableCell className="max-w-[200px] truncate">{ip.reason}</TableCell>
                              <TableCell>
                                <div className="text-xs">{new Date(ip.createdAt).toLocaleDateString()}</div>
                                {ip.blockExpires && (
                                  <div className="text-xs text-muted-foreground">
                                    Expires: {new Date(ip.blockExpires).toLocaleDateString()}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    toast({
                                      title: "IP unblocked",
                                      description: `${ip.ipAddress} has been unblocked`,
                                    });
                                  }}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Blocked Users */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Flagged Users</CardTitle>
                      <CardDescription>
                        Users that have been flagged for suspicious activity
                      </CardDescription>
                    </div>
                    <Button size="sm">
                      <Flag className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {blockedUsersLoading ? (
                    <div className="text-center py-10">
                      <div className="animate-spin w-8 h-8 rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Loading flagged users...</p>
                    </div>
                  ) : blockedUsers.length === 0 ? (
                    <div className="text-center py-10">
                      <Flag className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                      <h3 className="text-base font-medium mb-1">No flagged users</h3>
                      <p className="text-sm text-muted-foreground">
                        There are no flagged users
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead>Reason</TableHead>
                            <TableHead>Date Flagged</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {blockedUsers.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell className="font-medium">{user.userName}</TableCell>
                              <TableCell className="max-w-[200px] truncate">{user.reason}</TableCell>
                              <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    toast({
                                      title: "User unflagged",
                                      description: `${user.userName} has been unflagged`,
                                    });
                                  }}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Fraud Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Stripe Radar Settings</CardTitle>
                <CardDescription>
                  Configure Stripe Radar integration settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="radar-mode">Radar Mode</Label>
                  <Select defaultValue="automatic">
                    <SelectTrigger id="radar-mode">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="automatic">Automatic</SelectItem>
                      <SelectItem value="manual">Manual Review</SelectItem>
                      <SelectItem value="strict">Strict (Block More)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Control how Stripe Radar evaluates risk for your transactions
                  </p>
                </div>

                <div>
                  <Label htmlFor="risk-threshold">Risk Score Threshold</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="risk-threshold"
                      type="number"
                      min="0"
                      max="100"
                      defaultValue="75"
                      className="w-24"
                    />
                    <div className="flex-1">
                      <Progress value={75} className="h-2" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Transactions with risk scores above this threshold will be flagged
                  </p>
                </div>

                <div>
                  <Label className="mb-2 block">Fraud Monitoring</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="radar-events" defaultChecked />
                      <label htmlFor="radar-events" className="text-sm">
                        Monitor Radar Early Fraud Warnings
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="dispute-events" defaultChecked />
                      <label htmlFor="dispute-events" className="text-sm">
                        Monitor Charge Disputes
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="blocked-payments" defaultChecked />
                      <label htmlFor="blocked-payments" className="text-sm">
                        Monitor Blocked Payments
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="high-risk-countries" defaultChecked />
                      <label htmlFor="high-risk-countries" className="text-sm">
                        Block high-risk countries
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button>
                  Save Settings
                </Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Notification Settings</CardTitle>
                <CardDescription>
                  Configure fraud notification preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="notification-email">Notification Email</Label>
                    <Input
                      id="notification-email"
                      type="email"
                      defaultValue="security@example.com"
                    />
                  </div>

                  <div>
                    <Label className="mb-2 block">Email Notifications</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox id="email-high" defaultChecked />
                        <label htmlFor="email-high" className="text-sm">
                          High severity events
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="email-medium" defaultChecked />
                        <label htmlFor="email-medium" className="text-sm">
                          Medium severity events
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="email-low" defaultChecked={false} />
                        <label htmlFor="email-low" className="text-sm">
                          Low severity events
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="digest-frequency">Summary Digest Frequency</Label>
                    <Select defaultValue="daily">
                      <SelectTrigger id="digest-frequency">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="never">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button>
                  Save Notification Settings
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Fraud Event Details Dialog */}
        {selectedEvent && (
          <Dialog open={showEventDetails} onOpenChange={setShowEventDetails}>
            <DialogContent className="sm:max-w-[700px]">
              <DialogHeader>
                <DialogTitle>Fraud Event Details</DialogTitle>
                <DialogDescription>
                  Event ID: {selectedEvent.id}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-2">Event Information</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Description</p>
                        <p>{selectedEvent.description}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Type</p>
                        <div>{getTypeBadge(selectedEvent.type)}</div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Severity</p>
                        <div>{getSeverityBadge(selectedEvent.severity)}</div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <div>{getStatusBadge(selectedEvent.status)}</div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Created</p>
                        <p>{new Date(selectedEvent.createdAt).toLocaleString()}</p>
                      </div>
                      {selectedEvent.stripeEventId && (
                        <div>
                          <p className="text-sm text-muted-foreground">Stripe Event ID</p>
                          <p className="text-xs font-mono">{selectedEvent.stripeEventId}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-2">Related Information</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Tenant</p>
                        <p>{selectedEvent.tenantName}</p>
                      </div>
                      {selectedEvent.userName && (
                        <div>
                          <p className="text-sm text-muted-foreground">User</p>
                          <p>{selectedEvent.userName}</p>
                        </div>
                      )}
                      {selectedEvent.ipAddress && (
                        <div>
                          <p className="text-sm text-muted-foreground">IP Address</p>
                          <div className="flex">
                            <p className="font-mono">{selectedEvent.ipAddress}</p>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2 ml-2"
                              onClick={() => {
                                setBlockIPForm({
                                  ...blockIPForm,
                                  ipAddress: selectedEvent.ipAddress || ''
                                });
                                setShowEventDetails(false);
                                setShowBlockAddressDialog(true);
                              }}
                            >
                              Block
                            </Button>
                          </div>
                        </div>
                      )}
                      {selectedEvent.amount !== null && (
                        <div>
                          <p className="text-sm text-muted-foreground">Amount</p>
                          <p>{formatCurrency(selectedEvent.amount, selectedEvent.currency || 'USD')}</p>
                        </div>
                      )}
                    </div>

                    {selectedEvent.metadata && Object.keys(selectedEvent.metadata).length > 0 && (
                      <div className="mt-4">
                        <h3 className="font-semibold mb-2">Additional Details</h3>
                        <div className="bg-muted p-3 rounded-md text-xs font-mono h-[150px] overflow-y-auto">
                          <pre className="whitespace-pre-wrap break-all">
                            {JSON.stringify(selectedEvent.metadata, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="font-semibold mb-2">Action Notes</h3>
                  <Textarea 
                    placeholder="Add notes about actions taken for this event..."
                    className="h-[80px]"
                  />
                </div>
              </div>
              <DialogFooter className="flex flex-wrap gap-2">
                {selectedEvent.status === "new" && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleUpdateEventStatus(selectedEvent.id, "reviewing");
                      setShowEventDetails(false);
                    }}
                  >
                    <Clock className="mr-2 h-4 w-4" />
                    Mark as Reviewing
                  </Button>
                )}
                
                {(selectedEvent.status === "new" || selectedEvent.status === "reviewing") && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleUpdateEventStatus(selectedEvent.id, "resolved");
                      setShowEventDetails(false);
                    }}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Mark as Resolved
                  </Button>
                )}
                
                {(selectedEvent.status === "new" || selectedEvent.status === "reviewing") && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleUpdateEventStatus(selectedEvent.id, "dismissed");
                      setShowEventDetails(false);
                    }}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Dismiss
                  </Button>
                )}
                
                <Button
                  variant="default"
                  onClick={() => setShowEventDetails(false)}
                >
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Block IP Address Dialog */}
        <Dialog open={showBlockAddressDialog} onOpenChange={setShowBlockAddressDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Block IP Address</DialogTitle>
              <DialogDescription>
                Block an IP address from accessing the platform
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label htmlFor="ip-address">IP Address</Label>
                <Input 
                  id="ip-address" 
                  value={blockIPForm.ipAddress}
                  onChange={(e) => setBlockIPForm({...blockIPForm, ipAddress: e.target.value})}
                  placeholder="e.g. 203.0.113.1"
                />
              </div>
              
              <div>
                <Label htmlFor="block-reason">Reason</Label>
                <Textarea 
                  id="block-reason"
                  value={blockIPForm.reason}
                  onChange={(e) => setBlockIPForm({...blockIPForm, reason: e.target.value})}
                  placeholder="Why is this IP being blocked?"
                />
              </div>
              
              <div>
                <Label htmlFor="block-expiration">Block Duration</Label>
                <Select 
                  value={blockIPForm.expiration}
                  onValueChange={(value) => setBlockIPForm({...blockIPForm, expiration: value})}
                >
                  <SelectTrigger id="block-expiration">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24h">24 Hours</SelectItem>
                    <SelectItem value="7d">7 Days</SelectItem>
                    <SelectItem value="30d">30 Days</SelectItem>
                    <SelectItem value="never">Permanent (Never Expires)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBlockAddressDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleBlockIPAddress}
                disabled={!blockIPForm.ipAddress || !blockIPForm.reason}
              >
                Block IP
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SuperAdminLayout>
  );
}

// Helper Components
interface CheckboxProps {
  id: string;
  defaultChecked?: boolean;
  [key: string]: any;
}

const Checkbox = ({ id, defaultChecked, ...props }: CheckboxProps) => {
  return (
    <input 
      type="checkbox" 
      id={id} 
      defaultChecked={defaultChecked}
      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
      {...props}
    />
  );
};

interface IconProps {
  [key: string]: any;
}

const BlockIcon = (props: IconProps) => {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
};